import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../utils/apiConfig';

const PermisosContext = createContext();

export function usePermisos() {
  return useContext(PermisosContext);
}

export function PermisosProvider({ children }) {
  const [user, setUser] = useState(null);
  const [idComerciales, setIdComerciales] = useState(null); // null = ver todo
  const [loadingPermisos, setLoadingPermisos] = useState(true);
  const [visitas, setVisitas] = useState([]);
  const [loadingVisitas, setLoadingVisitas] = useState(true);
  const [obras, setObras] = useState([]);
  const [loadingObras, setLoadingObras] = useState(true);
  const [funcionComercial, setFuncionComercial] = useState(null);
  const [comercialIdsPermitidos, setComercialIdsPermitidos] = useState([]);
  const [ubicacionesPermitidas, setUbicacionesPermitidas] = useState([]);
  const [idsBloquesPermitidos, setIdsBloquesPermitidos] = useState([]);
  const [datosComerciales, setDatosComerciales] = useState([]);
  const [infoComerciales, setInfoComerciales] = useState([]);
  const [idsSeccionesPermitidos, setIdsSeccionesPermitidos] = useState([]);
  
  // 🔐 FLAGS DE ACCESO
  const [tieneAccesoVisitas, setTieneAccesoVisitas] = useState(false);
  const [tieneAccesoObras, setTieneAccesoObras] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false); // 🔒 Nuevo: detectar si es admin


  // 1. Obtener usuario y permisos
  useEffect(() => {
  const fetchUserAndPermisos = async () => {
    setLoadingPermisos(true);
    try {
      // console.log('[PermisosContext] Obteniendo datos de usuario...');
      const userRes = await fetch(getApiUrl('/api/dashboard-data'), { credentials: 'include' });
      if (!userRes.ok) {
        console.error('[PermisosContext] Error al obtener usuario:', userRes.status);
        throw new Error('No user');
      }
      const userData = await userRes.json();
      setUser(userData.user);
      const email = userData.user?.username;
      const name = userData.user?.name;
      if (!email) {
        setIdComerciales(null);
        setLoadingPermisos(false);
        return;
      }

      // console.log('[PermisosContext] Email para permisos:', email);

      // const permisosRes = await fetch(`http://localhost:5000/api/permisos?email=${encodeURIComponent(email)}`);
      const permisosRes = await fetch(getApiUrl(`/api/permisos?email=${encodeURIComponent(email)}&name=${encodeURIComponent(name)}`));
      if (!permisosRes.ok) throw new Error('No permisos');
      const permisosData = await permisosRes.json();
      // // console.log('[PermisosContext] Resultado permisos:', permisosData);

      // 🔒 Guardar si es Admin (para bloquear edición)
      setIsAdmin(permisosData.isAdmin || false);

      // ===== PERMISOS DE VISITAS =====
      if (permisosData.isAdmin) {
        // ✅ ADMIN: Puede ver TODAS las visitas
        setIdComerciales(null);
        setComercialIdsPermitidos([]);
        setDatosComerciales([]);
        setTieneAccesoVisitas(true);
        // console.log('[PermisosContext] ✅ ADMIN - Acceso total a visitas (solo lectura)');
      } else if (Array.isArray(permisosData.ids) && permisosData.ids.length > 0) {
        // ✅ Usuario con permisos específicos para visitas
        setIdComerciales(permisosData.ids);
        setComercialIdsPermitidos(permisosData.ids);
        setTieneAccesoVisitas(true);
        // console.log('[PermisosContext] ✅ Acceso a visitas - IDs comerciales:', permisosData.ids);
        // Fetch detalles de los comerciales permitidos
        try {
          const resComerciales = await fetch(getApiUrl(`/api/comerciales2?ids=${permisosData.ids.join(',')}`));
          if (resComerciales.ok) {
            const dataComerciales = await resComerciales.json();
            setDatosComerciales(dataComerciales);
            // console.log('[PermisosContext] Comerciales con nombre:', dataComerciales);
          } else {
            console.warn('[PermisosContext] No se pudieron obtener los datos de comerciales');
            setDatosComerciales([]);
          }
        } catch (error) {
          console.error('[PermisosContext] Error al obtener comerciales:', error);
          setDatosComerciales([]);
        }
      } else {
        // ❌ Usuario SIN acceso a visitas
        setIdComerciales('NO_ACCESS');
        setComercialIdsPermitidos('NO_ACCESS');
        setDatosComerciales([]);
        setTieneAccesoVisitas(false);
        // console.log('[PermisosContext] ❌ Sin acceso a visitas');
      }

      // Ubicaciones
      if (Array.isArray(permisosData.idsUbicaciones) && permisosData.idsUbicaciones.length > 0) {
        setUbicacionesPermitidas(permisosData.idsUbicaciones);
        // console.log('[PermisosContext] IDs ubicaciones permitidas:', permisosData.idsUbicaciones);
      } else {
        setUbicacionesPermitidas(null); // ver todo
      }

      // ===== PERMISOS DE OBRAS =====
      if (permisosData.isAdmin) {
        // ✅ ADMIN: Puede ver TODAS las obras
        setIdsBloquesPermitidos(null);
        setTieneAccesoObras(true);
        // console.log('[PermisosContext] ✅ ADMIN - Acceso total a obras');
      } else if (Array.isArray(permisosData.idsBloquesPermitidos) && permisosData.idsBloquesPermitidos.length > 0) {
        // ✅ Usuario con permisos específicos para obras
        setIdsBloquesPermitidos(permisosData.idsBloquesPermitidos);
        setTieneAccesoObras(true);
        // console.log('[PermisosContext] ✅ Acceso a obras - Bloques:', permisosData.idsBloquesPermitidos);
      } else {
        // ❌ Usuario SIN acceso a obras
        setIdsBloquesPermitidos('NO_ACCESS');
        setTieneAccesoObras(false);
        // console.log('[PermisosContext] ❌ Sin acceso a obras');
      }

      // ===== UBICACIONES (para crear obras) =====
      if (Array.isArray(permisosData.idsUbicaciones) && permisosData.idsUbicaciones.length > 0) {
        setUbicacionesPermitidas(permisosData.idsUbicaciones);
        // console.log('[PermisosContext] Ubicaciones para crear obras:', permisosData.idsUbicaciones);
      } else {
        setUbicacionesPermitidas(permisosData.isAdmin ? null : []); // Admin = todas, otros = ninguna
      }

      // ===== DELEGACIONES/SECCIONES =====
      if (Array.isArray(permisosData.idsSecciones) && permisosData.idsSecciones.length > 0) {
        setIdsSeccionesPermitidos(permisosData.idsSecciones);
        // console.log('[PermisosContext] Delegaciones del usuario:', permisosData.idsSecciones);
      } else {
        setIdsSeccionesPermitidos(permisosData.isAdmin ? null : []); // Admin = todas, otros = ninguna
      }

      // ===== INFO DEL COMERCIAL CONECTADO =====
      if (Array.isArray(permisosData.comercialInfo) && permisosData.comercialInfo.length > 0) {
        setInfoComerciales(permisosData.comercialInfo);
        // console.log('[PermisosContext] Info comercial conectado:', permisosData.comercialInfo);
      } else {
        setInfoComerciales([]);
        // console.log('[PermisosContext] Sin info comercial');
      }

      setFuncionComercial(permisosData.funcionComercial || null);
    } catch (err) {
      console.error('[PermisosContext] Error:', err);
      setIdComerciales(null); // fallback
      setUbicacionesPermitidas(null);
      setIdComerciales(null);
    } finally {
      setLoadingPermisos(false);
    }
  };

  fetchUserAndPermisos();
  }, []);

  // 2 y 3. Obtener visitas y obras en paralelo cuando se tengan los permisos
  useEffect(() => {
    if (loadingPermisos) return;
    
    const fetchVisitasYObras = async () => {
      setLoadingVisitas(true);
      setLoadingObras(true);
      
      // Ejecutar ambas peticiones en paralelo
      const [visitasResult, obrasResult] = await Promise.allSettled([
        // Fetch Visitas
        (async () => {
          try {
            // Verificar permisos de visitas
            if (idComerciales === 'NO_ACCESS') {
              // console.log('[PermisosContext] Usuario sin acceso a visitas');
              return [];
            }
            
            let url;
            // 🔥 Si es ADMIN (idComerciales === null), usar endpoint especial para TODAS las visitas
            if (idComerciales === null) {
              url = getApiUrl('/api/visitasA');
              // // console.log('[PermisosContext] 👑 ADMIN - Obteniendo TODAS las visitas desde /api/visitasA');
            } else if (Array.isArray(idComerciales) && idComerciales.length > 0) {
              // Usuario normal con IDs específicos
              url = getApiUrl('/api/visitas?idComerciales=' + idComerciales.join(','));
              // console.log('[PermisosContext] 👤 Usuario con permisos - IDs:', idComerciales);
            } else {
              // Caso edge: tiene acceso pero sin IDs (no debería pasar)
              console.warn('[PermisosContext] ⚠️ Tiene acceso a visitas pero sin IDs');
              return [];
            }
            
            // console.log('[PermisosContext] URL para visitas:', url);
            const res = await fetch(url, { credentials: 'include' });
            if (res.ok) {
              const data = await res.json();
              // console.log('[PermisosContext] ✅ Visitas obtenidas:', data.length);
              return data;
            }
            return [];
          } catch (error) {
            console.error('[PermisosContext] ❌ Error al obtener visitas:', error);
            return [];
          }
        })(),
        
        // Fetch Obras
        (async () => {
          try {
            // Verificar permisos de obras
            if (idsBloquesPermitidos === 'NO_ACCESS') {
              // console.log('[PermisosContext] Usuario sin acceso a obras');
              return [];
            }
            
            let url = getApiUrl('/api/obras');
            
            if (Array.isArray(idsBloquesPermitidos)) {
              const bloquesFiltrados = idsBloquesPermitidos.filter(id => id && id.trim() !== '');
              if (bloquesFiltrados.length > 0) {
                url += '?IdBloque=' + bloquesFiltrados.join(',');
              }
            }

            // console.log('[PermisosContext] URL para obras:', url);
            const res = await fetch(url, { credentials: 'include' });
            if (res.ok) {
              const data = await res.json();
              // console.log('[PermisosContext] Obras obtenidas:', data.length);
              return data;
            }
            return [];
          } catch (error) {
            console.error('[PermisosContext] Error al obtener obras:', error);
            return [];
          }
        })()
      ]);
      
      // Actualizar estados con los resultados
      setVisitas(visitasResult.status === 'fulfilled' ? visitasResult.value : []);
      setObras(obrasResult.status === 'fulfilled' ? obrasResult.value : []);
      
      setLoadingVisitas(false);
      setLoadingObras(false);
      
      // console.log('[PermisosContext] ✅ Carga paralela completada');
    };
    
    fetchVisitasYObras();
  }, [idComerciales, idsBloquesPermitidos, loadingPermisos]);


  // Actualizar tabla de visitas al agregar nueva visita
  const refreshVisitas = async () => {
    // Verificar permisos antes de hacer la petición
    if (idComerciales === 'NO_ACCESS') {
      setVisitas([]);
      return;
    }
    
    let url;
    // 🔥 Si es ADMIN (idComerciales === null), usar endpoint especial para TODAS las visitas
    if (idComerciales === null) {
      url = getApiUrl('/api/visitasA');
      // console.log('[PermisosContext] 👑 ADMIN - Refrescando TODAS las visitas desde /api/visitasA');
    } else if (Array.isArray(idComerciales) && idComerciales.length > 0) {
      url = getApiUrl('/api/visitas?idComerciales=' + idComerciales.join(','));
      // console.log('[PermisosContext] 👤 Usuario con permisos - Refrescando IDs:', idComerciales);
    } else {
      console.warn('[PermisosContext] ⚠️ Tiene acceso pero sin IDs para refresh');
      setVisitas([]);
      return;
    }
    
    const res = await fetch(url, { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setVisitas(data);
      // console.log('[PermisosContext] ✅ Visitas actualizadas:', data.length);
    } else {
      setVisitas([]);
      console.error('[PermisosContext] ❌ Error al actualizar visitas');
    }
  };

  const refreshObras = async () => {
    // Verificar permisos antes de hacer la petición
    if (idsBloquesPermitidos === 'NO_ACCESS') {
      setObras([]);
      return;
    }
    
    let url = getApiUrl('/api/obras');
    if (idsBloquesPermitidos && Array.isArray(idsBloquesPermitidos) && idsBloquesPermitidos.length > 0) {
      url += '?IdBloque=' + idsBloquesPermitidos.join(',');
    }
    // console.log('[PermisosContext] URL para actualizar obras:', url);
    const res = await fetch(url, { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setObras(data);
    } else {
      setObras([]);
    }
    // console.log('[PermisosContext] Obras actualizadas:', obras);
  };

  return (
    <PermisosContext.Provider value={{ 
      user, 
      loadingPermisos, 
      funcionComercial, 
      datosComerciales,
      infoComerciales,

      visitas, 
      setVisitas, 
      loadingVisitas, 
      refreshVisitas, 
      idComerciales,
      comercialIdsPermitidos, 

      obras, 
      setObras,
      loadingObras,
      refreshObras,
      ubicacionesPermitidas, 
      idsBloquesPermitidos,
      idsSeccionesPermitidos,
      
      // 🔐 FLAGS DE ACCESO
      tieneAccesoVisitas,
      tieneAccesoObras,
      isAdmin // 🔒 Nuevo: flag para deshabilitar edición
    }}>
      {children}
    </PermisosContext.Provider>
  );
}
